import {Switch} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import refreshAnalysis from 'analysis-tool/refreshAnalysis';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepTypeMenu from 'pipelines/components/StepTypeMenu';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import {PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {getLatestStepVersion, getPossibleEdges, getSourceStepTypes} from 'pipelines/StepConnectionUtils';
import {MenuPanelSection} from 'ui-library/menus/MenuPanel';
import PlainSearchInput, {BorderRadius, ClearType} from 'ui-library/PlainSearchInput';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../../RightSidebarTypes';
import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';

/** Step types that are dangerous to add, even though they are supported by the step config editor. */
// Keep in sync with _NON_ADDABLE_STEP_TYPES in benchling/pipelines/api/pipelines.py
const NON_ADDABLE_STEP_TYPES: Partial<Record<PipelineStepType, boolean>> = {
  // While CHART_VIEW is supported by the config editor, only certain charts types are supported.
  [PipelineStepType.CHART_VIEW]: true,
  // CONNECTION_DATA_DATASET is gated behind the connection dataset creation flag
  [PipelineStepType.CONNECTION_DATA_DATASET]:
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_CONNECTION_DATASET_CREATION,
  // CREATE_METERING_USAGE_CHART has a step config UI, but is for use in internal admin
  [PipelineStepType.CREATE_METERING_USAGE_CHART]: true,
  // DEFINE_TEMPLATE_VARIABLES is available as a beta feature
  [PipelineStepType.DEFINE_TEMPLATE_VARIABLES]: !BenchlingConfig.ENABLE_INSIGHTS_LABS,
  // PARAMETER_OPTIMIZATION_CONFIGURATION steps cannot be removed once added.
  [PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION]: true,
  // PLATE_SCHEMA_DATASET is gated behind the experimental schemas flag
  [PipelineStepType.PLATE_SCHEMA_DATASET]: !BenchlingConfig.DOMAIN_GRAPH_ENABLE_EXPERIMENTAL_SCHEMAS,
  // RUN_HIGHRES_PROTOCOL steps are an experimental feature
  [PipelineStepType.RUN_HIGHRES_PROTOCOL]:
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_INTERACTIVE_FLOWCHART_EXPERIMENTAL,
  [PipelineStepType.EXTRACT_DATA_FRAME_VALUE]:
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_EXTRACT_DATA_FRAME_VALUE_STEP,
  // TODO(BNCH-145917): SEND_USER_NOTIFICATION is still in development
  [PipelineStepType.SEND_USER_NOTIFICATION]: true,
  // WAREHOUSE_SQL_DATASET is gated behind the ANALYSIS_DIRECT_WAREHOUSE_SQL flag
  [PipelineStepType.WAREHOUSE_SQL_DATASET]: !BenchlingConfig.ANALYSIS_DIRECT_WAREHOUSE_SQL,
};

/**
 * True if the step type is safe to and and edit in the generic way.
 */
function canAddStepType(stepType: PipelineStepType): boolean {
  // Don't allow adding steps which can't be configured in the generic way.
  return (
    stepTypeIsConfigEditorSupported(stepType, getLatestStepVersion(stepType)) &&
    !NON_ADDABLE_STEP_TYPES[stepType]
  );
}

/**
 * True if the step type can be connected to `step` and `stepData`.
 */
function canConnectStepTypeToStepData(
  step: PipelineStep,
  stepData: PipelineStepData,
  newStepType: PipelineStepType
): boolean {
  const possibleEdges = step.inputStepDatas.find(({id}) => id === stepData.id)
    ? getPossibleEdges({
        fromStepType: newStepType,
        toStepType: step.stepType,
        toStepData: {systemName: stepData.name},
      })
    : getPossibleEdges({
        toStepType: newStepType,
        fromStepType: step.stepType,
        fromStepData: {systemName: stepData.name},
      });

  // There may be more than one possible connection to the next step, such as to either the `Left` or `Right`
  // input of a join. In this scenario, the `addStep` util will choose a valid edge, and the user can
  // drag-and-drop edges to fix any incorrect connections.
  return possibleEdges.length > 0;
}

/**
 * Add a new step to the pipeline.
 *
 * If `selectedRightSidebarItem.stepId` and `selectedRightSidebarItem.stepDataName` are set, the new step
 * will be connected to the step data.
 *
 * Only step types can be edited from `AnalysisFlowchart` can be added.
 *
 */
export default function AddStepRightSidebar({
  analysis: pipeline,
  onChangeSelectedRightSidebarItem,
  selectedRightSidebarItem,
}: RightSidebarComponentProps<
  AnalysisRightSidebarItemType.ADD_STEP | AnalysisRightSidebarItemType.ADD_SOURCE_STEP
>): JSX.Element {
  const {stepId, stepDataName} =
    selectedRightSidebarItem.type === AnalysisRightSidebarItemType.ADD_STEP
      ? selectedRightSidebarItem
      : {stepId: null, stepDataName: null};
  const [selectedStepType, setSelectedStepType] = useState<PipelineStepType | null>(null);
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const step = stepId ? getStepFromAnalysis(pipeline, stepId) : null;
  const stepData = step
    ? [...step.inputStepDatas, ...step.outputStepDatas].find(({name}) => name === stepDataName)
    : undefined;

  /**
   * If there is step info, only allow step types which can be connected to `step` and `stepData`; otherwise,
   * only allow adding source steps.
   */
  const sourceStepTypes = getSourceStepTypes();
  const filterStepTypes = (stepType: PipelineStepType): boolean =>
    canAddStepType(stepType) &&
    (step && stepData
      ? canConnectStepTypeToStepData(step, stepData, stepType)
      : sourceStepTypes.includes(stepType));

  const extraSections: Array<MenuPanelSection> | undefined =
    selectedRightSidebarItem.type === AnalysisRightSidebarItemType.ADD_SOURCE_STEP
      ? [
          {
            header: 'External data',
            rows: [
              {
                id: PipelineStepType.THIRD_PARTY,
                label: 'Connect with external tool',
                description: 'Integrates external tools and services into your analysis workflow.',
              },
            ],
          },
        ]
      : undefined;

  // Add the new step type when Next is clicked
  const onNext = async (): Promise<void> => {
    if (!selectedStepType) {
      return;
    }
    if (selectedStepType === PipelineStepType.THIRD_PARTY) {
      onChangeSelectedRightSidebarItem({
        type: AnalysisRightSidebarItemType.THIRD_PARTY_IMPORT,
      });
      return;
    }
    const {stepId} = await withLoadingScreen(
      addStepWithInferredEdges(pipeline, {stepType: selectedStepType, stepData})
    );
    await withLoadingScreen(refreshAnalysis(pipeline.id));
    onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId});
  };

  const title =
    selectedRightSidebarItem.type === AnalysisRightSidebarItemType.ADD_SOURCE_STEP
      ? 'Add source step'
      : 'Add step';

  return (
    <RightSidebarSkeleton
      title={title}
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem(null);
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Next',
          onClick: onNext,
          dataTestAttrsElement: 'next',
          disabled: !selectedStepType,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.MEDIUM}
      dataTestAttrs={{
        component: 'AddStepRightSidebar',
        element: 'root',
      }}
    >
      <div className={css(styles.container)}>
        <div className={css(styles.searchBar)}>
          <PlainSearchInput
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            onEscape={() => {
              setSearchQuery('');
            }}
            clearType={ClearType.X}
            borderRadius={BorderRadius.LARGE}
            className={css(styles.searchInput)}
          />
        </div>
        <div className={css(styles.menuWrapper)}>
          <StepTypeMenu
            filterStepTypes={filterStepTypes}
            selectedStepType={selectedStepType}
            onSelectStepType={setSelectedStepType}
            showDescription={showDescriptions}
            searchQuery={searchQuery}
            extraSections={extraSections}
          />
          <div className={css(styles.footer)}>
            <Switch
              label="Step descriptions"
              checked={showDescriptions}
              onChange={(e) => {
                setShowDescriptions(e.currentTarget.checked);
              }}
              styles={{
                root: {justifyContent: 'flex-end'},
                body: {alignItems: 'center'},
              }}
            />
          </div>
        </div>
      </div>
    </RightSidebarSkeleton>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  searchBar: {
    flexShrink: 0,
    paddingBottom: '12px',
  },
  searchInput: {
    width: '100%',
  },
  menuWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  footer: {
    flexShrink: 0,
    padding: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});
